/**
 * @fileoverview Staff Note Hook - Manages ingestion notes as staff notes.
 *
 * Ingestion notes (intake and general) are stored as separate staff notes
 * with header prefixes like "From Case Ingestion - Intake Notes". This hook
 * handles finding, parsing, and saving these notes.
 */
import { useCallback, useMemo } from 'react';

import {
  useCreateStaffNoteMutation,
  useGetStaffNotesQuery,
  useUpdateStaffNoteMutation,
} from '@/services/api/staffNotesService';

import {
  extractNoteContent,
  formatIngestionNote,
  GENERAL_NOTES_HEADER,
  type IngestionNotesData,
  INTAKE_NOTES_HEADER,
} from '../utils/notesParser';

/**
 * Manages ingestion notes stored as separate staff notes.
 *
 * Each note type (intake, general) is stored as its own staff note with a
 * header prefix like "From Case Ingestion - Intake Notes".
 *
 * @param matterId - The matter ID to manage notes for
 */
export function useIngestionStaffNote(matterId: string) {
  const { data: staffNotes, isLoading } = useGetStaffNotesQuery(
    { matterId },
    { skip: !matterId },
  );

  const [createStaffNote, { isLoading: isCreating }] =
    useCreateStaffNoteMutation();
  const [updateStaffNote, { isLoading: isUpdating }] =
    useUpdateStaffNoteMutation();

  // Find intake notes staff note
  const intakeNote = useMemo(
    () => staffNotes?.find((n) => n.content.startsWith(INTAKE_NOTES_HEADER)),
    [staffNotes],
  );

  // Find general notes staff note
  const generalNote = useMemo(
    () => staffNotes?.find((n) => n.content.startsWith(GENERAL_NOTES_HEADER)),
    [staffNotes],
  );

  // Parse existing data from both notes
  const existingData = useMemo(
    () => ({
      intakeNotes: intakeNote
        ? extractNoteContent(intakeNote.content, INTAKE_NOTES_HEADER)
        : '',
      generalNotes: generalNote
        ? extractNoteContent(generalNote.content, GENERAL_NOTES_HEADER)
        : '',
    }),
    [intakeNote, generalNote],
  );

  // Save function: create or update both notes
  const saveIngestionNotes = useCallback(
    async (data: IngestionNotesData) => {
      const promises: Promise<unknown>[] = [];

      // Save intake note (always save, even if empty - keeps header)
      const intakeContent = formatIngestionNote(
        INTAKE_NOTES_HEADER,
        data.intakeNotes,
      );
      if (intakeNote) {
        promises.push(
          updateStaffNote({
            matterId,
            noteId: intakeNote.id,
            note: { content: intakeContent },
          }).unwrap(),
        );
      } else {
        promises.push(
          createStaffNote({
            matterId,
            note: { content: intakeContent },
          }).unwrap(),
        );
      }

      // Save general note (always save, even if empty - keeps header)
      const generalContent = formatIngestionNote(
        GENERAL_NOTES_HEADER,
        data.generalNotes,
      );
      if (generalNote) {
        promises.push(
          updateStaffNote({
            matterId,
            noteId: generalNote.id,
            note: { content: generalContent },
          }).unwrap(),
        );
      } else {
        promises.push(
          createStaffNote({
            matterId,
            note: { content: generalContent },
          }).unwrap(),
        );
      }

      await Promise.all(promises);
    },
    [matterId, intakeNote, generalNote, createStaffNote, updateStaffNote],
  );

  return {
    /** Whether staff notes are still loading */
    isLoading,
    /** Whether a save operation is in progress */
    isSaving: isCreating || isUpdating,
    /** Parsed data from existing ingestion notes */
    existingData,
    /** Save function - creates or updates both staff notes */
    saveIngestionNotes,
  };
}
